/** @jsxImportSource @emotion/react */

import { useState, useRef } from 'react'
import _ from 'lodash'

import { useQuery, useMutation } from '@apollo/client'
import {
  GET_COMPANIES_SETTINGS,
  COMPANY_UPDATE,
  COMPANY_CREATE,
  DELETE_COMPANY
} from '../../graphql/company.graphql'
import { toast } from 'react-toastify'
import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Chip,
  TextField,
  FormControlLabel,
  Switch,
  Button,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import CloseIcon from '@material-ui/icons/Close'
import Skeleton from '@material-ui/lab/Skeleton'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { imageLink } from '../../utils/imageLink'
import { companyTypes } from '../../constants'
import CheckIcon from '@material-ui/icons/VerifiedUser'
import { colors } from '../../style'
import DeleteIcon from "@material-ui/icons/Delete";

const SettingsCompanies = () => {
  const { data, loading, error } = useQuery(GET_COMPANIES_SETTINGS)
  const [companyCreate] = useMutation(COMPANY_CREATE)
  const [companyUpdate] = useMutation(COMPANY_UPDATE)
  const [companyDelete] = useMutation(DELETE_COMPANY)
  const [open, setOpen] = useState(false)
  const tableRef = useRef(null)

  const scrollToBottom = () => {
    tableRef.current?.scrollTo({
      top: tableRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }

  if (loading)
    return <Skeleton variant='rect' height={300} css={{ width: '100%' }} />

  if (error) {
    toast.error(error.toString())
    return <div>{error.toString()}</div>
  }

  const onUpdateSubmit = ({ id, input, callback }) => {
    companyUpdate({
      variables: {
        id,
        input: {
          ..._.omit(input, ['__typename', '_id', 'projects']),
          cph_default: parseFloat(input.cph_default),
        },
      },
      refetchQueries: [{ query: GET_COMPANIES_SETTINGS }],
    })
      .then((res) => {
        if (res?.data?.companyUpdate)
          toast.info(
            <div>
              <strong>
                💼&nbsp;&nbsp;<span>UPDATED</span>
              </strong>
              <p>
                <strong>{input.name}</strong>
                {input.description && <span>&nbsp;({input.description})</span>}
              </p>
            </div>,
            { autoClose: 10000 }
          )
        callback()
      })
      .catch((error) => {
        toast.error(error.toString())
      })
  }

  const onCreateSubmit = ({ input, callback }) => {
    companyCreate({
      variables: {
        input: {
          ..._.pickBy(input),
          cph_default: parseFloat(input.cph_default),
        },
      },
      // refetchQueries: [{ query: GET_COMPANIES_SETTINGS }],
      update: (cache, { data }) => {
        const companiesCache = cache.readQuery({
          query: GET_COMPANIES_SETTINGS,
        })
        cache.writeQuery({
          query: GET_COMPANIES_SETTINGS,
          data: {
            getCompanies: [
              ...companiesCache?.getCompanies,
              data?.companyCreate,
            ],
          },
        })
      },
    })
      .then((res) => {
        if (res?.data?.companyCreate)
          toast.success(
            <div>
              <strong>
                💼&nbsp;&nbsp;<span>CREATED</span>
              </strong>
              <p>
                <strong>{input.name}</strong>
                {input.description && <span>&nbsp;({input.description})</span>}
              </p>
            </div>,
            { autoClose: 10000 }
          )
        callback()
        scrollToBottom()
      })
      .catch((error) => {
        toast.error(error.toString())
      })
  }

    const onDeleteSubmit = (companyId) => {

        if (window.confirm("Are you sure you want to delete this company?")) {
            companyDelete({
                variables: {id: companyId},
                refetchQueries: [{query: GET_COMPANIES_SETTINGS}],
            })
                .then((res) => {
                    if (res.data.companyDelete) {
                        toast.success(
                            <div>
                                <strong>
                                    🔽&nbsp;&nbsp;<span>DELETED</span>
                                </strong>
                                <p>
                                    The company was successfully removed
                                </p>
                            </div>,
                            {autoClose: 10000}
                        );
                    }
                })
                .catch((error) => {
                    toast.error(error.toString());
                });
        }
    }

  return (
    <>
      <TableContainer
        component={Paper}
        css={{ maxHeight: '50vh' }}
        ref={tableRef}>
        <Table stickyHeader aria-label='collapsible table' size='small'>
          <TableHead
            css={{
              th: { fontSize: '0.75rem', color: 'rgba(0,0,0,0.5)' },
            }}>
            <TableRow>
              <TableCell css={{ paddingLeft: '3rem' }}>Company</TableCell>
              <TableCell css={{ paddingLeft: '2rem' }} align='left'>
                Type
              </TableCell>
              <TableCell align='right'>Desc</TableCell>
              <TableCell align='center'>NIP</TableCell>
              <TableCell align='left'>Site</TableCell>
              <TableCell align='center'>CPH</TableCell>
              <TableCell align='center'>Project*</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.getCompanies.map((row, id) => (
              <Row key={row._id} data={row} onSubmit={onUpdateSubmit} onDelete={onDeleteSubmit} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Paper>
        <Collapse in={open}>
          <Form
            open={open}
            setOpen={setOpen}
            onSubmit={onCreateSubmit}
            button={{ title: 'CREATE' }}
          />
        </Collapse>
      </Paper>
      <Button
        css={{ marginTop: '1rem' }}
        size='small'
        onClick={() => setOpen(!open)}
        color={open ? 'default' : 'primary'}
        variant='contained'>
        {!open ? 'NEW COMPANY' : 'CANCEL CREATION'}
      </Button>
    </>
  )
}

const Row = ({ data, onSubmit, onDelete }) => {
  const [open, setOpen] = useState(false)
  const {
    name,
    description,
    websites,
    nip,
    cph_default,
    icon,
    isHidden,
    type,
    color,
    require_projects,
  } = data

  return (
    <>
      <TableRow
        css={[
          isHidden && { opacity: 0.25 },
          {
            td: { fontSize: '0.75rem' },
            '&:hover': {
              backgroundColor: '#fcfcfc',
            },
          },
        ]}>
        <TableCell component='th' scope='row'>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              fontSize: 'inherit',
            }}>
            {icon ? (
              <img
                src={imageLink(icon)}
                css={{ width: 16, height: 16 }}
                alt={name}
              />
            ) : (
              <div css={{ width: 16, height: 16, backgroundColor: 'white' }} />
            )}
            <span
              css={{
                paddingLeft: '1rem',
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}>
              {name}
            </span>
          </div>
        </TableCell>
        <TableCell align='left'>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <div
              css={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: color,
                marginRight: '0.5rem',
              }}
            />
            <span css={{ opacity: 0.33, fontSize: 10, fontWeight: 900 }}>
              {type}
            </span>
          </div>
        </TableCell>
        <TableCell align='right' css={{ opacity: 0.5, fontStyle: 'italic' }}>
          {description}
        </TableCell>
        <TableCell align='center'>{nip}</TableCell>
        <TableCell align='left'>
          <div css={{ display: 'flex', flexDirection: 'row' }}>
            {websites?.map((website, id) => (
              <a
                key={id}
                href={website}
                target='_blank'
                rel='noreferrer noopener'>
                <Chip
                  css={{ fontSize: '0.675rem' }}
                  size='small'
                  label={website.slice(website.includes('https://') ? 8 : 0)}
                />
              </a>
            ))}
          </div>
        </TableCell>
        <TableCell align='center' css={{ fontWeight: 'bold' }}>
          {cph_default}
        </TableCell>
        <TableCell align='center'>
          {require_projects ? (
            <CheckIcon fontSize='inheirt' css={{ color: colors.green400 }} />
          ) : (
            <CloseIcon fontSize='inheirt' css={{ color: colors.red }} />
          )}
        </TableCell>
        <TableCell>
            <IconButton aria-label="delete" size="small" onClick={() => onDelete(data._id)}>
                <DeleteIcon/>
            </IconButton>
          <IconButton
            aria-label='expand row'
            size='small'
            onClick={() => setOpen(!open)}>
            {open ? <CloseIcon /> : <EditIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow css={{ backgroundColor: '#f8f8f8', width: '100%' }}>
        <TableCell css={{ width: '100%', padding: 0, margin: 0 }} colSpan={10}>
          <Collapse
            in={open}
            timeout='auto'
            unmountOnExit
            css={{ width: '100%' }}>
            <Form
              open={open}
              setOpen={setOpen}
              data={data}
              onSubmit={onSubmit}
              button={{ title: 'UPDATE' }}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

const Form = ({ onSubmit, open, setOpen, data, button }) => {
  const initialValues = {
    name: '',
    description: '',
    nip: '',
    websites: [],
    type: 'OTHER',
    cph_default: 0,
    icon: '',
    color: '#' + Math.floor(Math.random() * 16777215).toString(16),
    isHidden: false,
    require_projects: false,
  }

  const [inputValues, setInputValues] = useState(
    open ? data || initialValues : initialValues
  )
  const handleOnChange = (e) =>
    setInputValues({ ...inputValues, [e.target.name]: e.target.value })

  return (
    <Grid
      container
      css={{
        width: '100%',
        padding: '1rem',
        justifyContent: 'space-between',
      }}
      spacing={2}>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          name='name'
          label='Name'
          type='text'
          required
          value={inputValues.name}
          onChange={handleOnChange}
          css={sInput}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <Autocomplete
          fullWidth
          value={inputValues.type}
          options={companyTypes}
          onChange={(event, newValue) => {
            setInputValues({
              ...inputValues,
              type: newValue,
            })
          }}
          renderOption={(option, { selected }) => (
            <div css={{ fontSize: '0.75rem' }}>{option}</div>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              variant='outlined'
              label='Type'
              type='text'
              size='small'
              css={sInput}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          name='description'
          label='Description'
          type='text'
          value={inputValues.description}
          onChange={handleOnChange}
          css={sInput}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={inputValues.websites}
          onChange={(event, newValue) => {
            setInputValues({ ...inputValues, websites: newValue })
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                size='small'
                variant='default'
                css={{ fontSize: '0.675rem' }}
                label={option}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              variant='outlined'
              label='Websites'
              placeholder='https://'
              css={sInput}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          name='nip'
          label='NIP'
          type='text'
          value={inputValues.nip}
          onChange={handleOnChange}
          css={sInput}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          name='cph_default'
          label='CPH'
          type='number'
          value={inputValues.cph_default}
          onChange={handleOnChange}
          css={sInput}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          name='icon'
          label='Icon'
          type='text'
          value={inputValues.icon}
          onChange={handleOnChange}
          css={sInput}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          name='color'
          label='Color'
          type='color'
          value={inputValues.color}
          onChange={handleOnChange}
          css={sInput}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <FormControlLabel
          css={{ span: { fontSize: '0.75rem' } }}
          control={
            <>
              <Switch
                name='isHidden'
                checked={inputValues.isHidden}
                onChange={() =>
                  setInputValues({
                    ...inputValues,
                    isHidden: !inputValues.isHidden,
                  })
                }
                color='primary'
              />
            </>
          }
          label='isHidden'
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <FormControlLabel
          css={{ span: { fontSize: '0.75rem' } }}
          control={
            <>
              <Switch
                name='require_projects'
                checked={inputValues.require_projects}
                onChange={() =>
                  setInputValues({
                    ...inputValues,
                    require_projects: !inputValues.require_projects,
                  })
                }
                color='primary'
              />
            </>
          }
          label='Require Project'
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <Button
          onClick={() =>
            onSubmit({ id: data?._id, input: inputValues, callback: setOpen })
          }
          size='small'
          variant='contained'
          disabled={!inputValues.name}
          css={{
            width: '100%',
            backgroundColor: 'green',
            color: 'white',
            fontSize: '0.75rem',
            '&:hover': {
              backgroundColor: 'green',
            },
          }}>
          {button.title}
        </Button>
      </Grid>
    </Grid>
  )
}

const sInput = {
  input: { fontSize: '0.75rem' },
  label: { fontSize: '0.75rem' },
}

export default SettingsCompanies
